import React, { useState, useEffect } from 'react';
import { useConfirm } from "../../hooks/useConfirm";
import { useNavigate } from 'react-router-dom';
import { clientesAPI } from '../../services/api';
import { planosAPI } from '../../services/planosService';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { Building, Plus, Edit, Trash2, CreditCard } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { PageSkeleton, SkeletonTabela } from '../../components/Skeleton';

const AdminClients = () => {
  const maskCnpj = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 11) return d.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    return d.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  };
  const maskCep = (v) => { const d = v.replace(/\D/g, '').slice(0, 8); return d.replace(/(\d{5})(\d)/, '$1-$2'); };
  const maskTel = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 10) return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
    return d.replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
  };
  const strip = (v) => (v || '').replace(/\D/g, '');
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const showConfirm = useConfirm();
  const [clients, setClients] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClientForPlan, setSelectedClientForPlan] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    cnpj: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    status: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientesRes, planosRes] = await Promise.all([
        clientesAPI.list(),
        planosAPI.list()
      ]);
      
      setPlanos(planosRes.data || []);
      
      // Buscar uso de cada cliente
      const clientesComUso = await Promise.all(
        (clientesRes.data || []).map(async (cliente) => {
          try {
            const usoRes = await planosAPI.getUso(cliente.id);
            return { ...cliente, uso: usoRes.data };
          } catch {
            return cliente;
          }
        })
      );
      
      setClients(clientesComUso);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        nome: formData.nome, 
        email: formData.email, 
        cnpj: strip(formData.cnpj),
        telefone: strip(formData.telefone),
        endereco: formData.endereco,
        cidade: formData.cidade,
        estado: formData.estado,
        cep: strip(formData.cep),
        status: formData.status ? 1 : 0 
      };
      
      if (editingClient) {
        await clientesAPI.update({ ...data, id: editingClient.id });
      } else {
        await clientesAPI.create(data);
      }
      
      toast.success('Cliente salvo com sucesso');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar');
    }
  };

  const handleDelete = async (id) => {
    showConfirm({
      title: 'Deletar Cliente',
      message: 'Tem certeza que deseja deletar este cliente?',
      confirmText: 'Deletar',
      type: 'danger',
      onConfirm: async () => {
        try {
          await clientesAPI.delete(id);
          toast.success('Cliente deletado com sucesso');
          loadData();
        } catch (error) {
          toast.error('Erro ao deletar');
        }
      }
    });
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      nome: client.nome || '',
      email: client.email || '',
      telefone: maskTel(client.telefone || ''),
      cnpj: maskCnpj(client.cnpj || ''),
      endereco: client.endereco || '',
      cidade: client.cidade || '',
      estado: client.estado || '',
      cep: maskCep(client.cep || ''),
      status: client.status === 1
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      email: '',
      telefone: '',
      cnpj: '',
      endereco: '',
      cidade: '',
      estado: '',
      cep: '',
      status: true
    });
    setEditingClient(null);
  };

  const openModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenPlanModal = (client) => {
    setSelectedClientForPlan(client);
    setShowPlanModal(true);
  };

  const handleAlterarPlano = async (planoId) => {
    if (!selectedClientForPlan) return;
    
    try {
      await planosAPI.alterarPlano(selectedClientForPlan.id, planoId);
      toast.success('Plano alterado com sucesso');
      setShowPlanModal(false);
      setSelectedClientForPlan(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao alterar plano');
    }
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Gerenciar Clientes">
          <SkeletonTabela colunas={6} linhas={10} />
        </PageSkeleton>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-2xl font-bold flex items-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Building className="mr-2" />
              Gerenciar Clientes
            </h1>
            <p className={`mt-1 text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Administre clientes do sistema
            </p>
          </div>
          <button
            onClick={openModal}
            className="secondary-button text-white px-4 py-2 rounded flex items-center"
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Cliente
          </button>
        </div>

        <div className={`rounded-lg shadow overflow-hidden ${
          darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}>
          <table className="min-w-full">
            <thead className={darkMode ? 'bg-zinc-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Nome</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Email</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Plano</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Uso Mensal</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Status</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase ${
                  darkMode ? 'text-gray-300' : 'text-gray-500'
                }`}>Ações</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${
              darkMode ? 'bg-zinc-800 divide-gray-700' : 'bg-white divide-gray-200'
            }`}>
              {clients.map((client) => (
                <tr key={client.id} className={darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-50'}>
                  <td 
                    className={`px-6 py-4 whitespace-nowrap text-sm font-medium cursor-pointer ${
                      darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'
                    }`}
                    onClick={() => navigate(`/admin/clients/${client.id}`)}
                  >
                    {client.nome}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>{client.email}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm`}>
                    <div className="flex items-center space-x-2">
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-500'}>
                        {client.uso?.plano_atual?.nome || 'Sem plano'}
                      </span>
                      <button
                        onClick={() => handleOpenPlanModal(client)}
                        className={`p-1 rounded-md transition-colors ${
                          darkMode 
                            ? 'text-purple-400 hover:text-purple-300 hover:bg-zinc-700' 
                            : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                        }`}
                        title="Alterar plano"
                      >
                        <CreditCard className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                    darkMode ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    {client.uso ? (
                      <div>
                        <div className="text-xs">
                          {client.uso.uso.mensagens_enviadas} / {client.uso.uso.limite_mensagens === -1 ? '∞' : client.uso.uso.limite_mensagens} msgs
                        </div>
                        <div className={`w-full bg-gray-200 rounded-full h-1.5 mt-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div 
                            className={`h-1.5 rounded-full ${
                              client.uso.uso.percentual_usado >= 90 ? 'bg-red-500' :
                              client.uso.uso.percentual_usado >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(client.uso.uso.percentual_usado, 100)}%` }}
                          />
                        </div>
                      </div>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      client.status === 1 
                        ? darkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                        : darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
                    }`}>
                      {client.status === 1 ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(client)}
                        className={`p-2 rounded-md transition-colors ${
                          darkMode 
                            ? 'text-blue-400 hover:text-blue-300 hover:bg-zinc-700' 
                            : 'text-blue-600 hover:text-blue-900 hover:bg-gray-100'
                        }`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal Editar Cliente */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 w-full max-w-md ${
              darkMode ? 'bg-zinc-800' : 'bg-white'
            }`}>
              <h2 className={`text-xl font-bold mb-4 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {editingClient ? 'Editar' : 'Novo'} Cliente
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Nome</label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>CPF/CNPJ</label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({...formData, cnpj: maskCnpj(e.target.value)})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white placeholder-gray-400'
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Telefone</label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => setFormData({...formData, telefone: maskTel(e.target.value)})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white placeholder-gray-400'
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="(00) 00000-0000"
                  />
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>Endereço</label>
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={(e) => setFormData({...formData, endereco: e.target.value})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white'
                        : 'border-gray-300 bg-white text-gray-900'
                    }`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      darkMode ? 'text-white' : 'text-gray-700'
                    }`}>Cidade</label>
                    <input
                      type="text"
                      value={formData.cidade}
                      onChange={(e) => setFormData({...formData, cidade: e.target.value})}
                      className={`w-full border rounded px-3 py-2 ${
                        darkMode
                          ? 'border-gray-600 bg-zinc-700 text-white'
                          : 'border-gray-300 bg-white text-gray-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      darkMode ? 'text-white' : 'text-gray-700'
                    }`}>Estado</label>
                    <input
                      type="text"
                      value={formData.estado}
                      onChange={(e) => setFormData({...formData, estado: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)})}
                      className={`w-full border rounded px-3 py-2 ${
                        darkMode
                          ? 'border-gray-600 bg-zinc-700 text-white placeholder-gray-400'
                          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                      }`}
                      maxLength="2"
                      placeholder="SP"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>CEP</label>
                  <input
                    type="text"
                    value={formData.cep}
                    onChange={(e) => setFormData({...formData, cep: maskCep(e.target.value)})}
                    className={`w-full border rounded px-3 py-2 ${
                      darkMode
                        ? 'border-gray-600 bg-zinc-700 text-white placeholder-gray-400'
                        : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                    }`}
                    placeholder="00000-000"
                  />
                </div>
                <div className="mb-4">
                  <label className={`flex items-center ${
                    darkMode ? 'text-white' : 'text-gray-700'
                  }`}>
                    <input
                      type="checkbox"
                      checked={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.checked})}
                      className="mr-2 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    Ativo
                  </label>
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className={`px-4 py-2 border rounded transition-colors ${
                      darkMode
                        ? 'text-gray-300 border-gray-600 hover:bg-zinc-700'
                        : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="gradient-button px-4 py-2 text-white rounded"
                  >
                    {editingClient ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Alterar Plano */}
        {showPlanModal && selectedClientForPlan && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto ${
              darkMode ? 'bg-zinc-800' : 'bg-white'
            }`}>
              <h2 className={`text-xl font-bold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Alterar Plano
              </h2>
              <p className={`text-sm mb-4 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Cliente: <span className="font-semibold">{selectedClientForPlan.nome}</span>
              </p>
              <p className={`text-sm mb-6 ${
                darkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Plano atual: <span className="font-semibold">
                  {selectedClientForPlan.uso?.plano_atual?.nome || 'Sem plano'}
                </span>
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {planos.map(plano => {
                  const isAtual = selectedClientForPlan.uso?.plano_atual?.id === plano.id;
                  return (
                    <div
                      key={plano.id}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isAtual
                          ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50'
                          : darkMode 
                            ? 'border-gray-700 hover:border-gray-600' 
                            : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {plano.nome}
                        </h3>
                        {isAtual && (
                          <span className="px-2 py-1 text-xs bg-blue-500 text-white rounded-full">
                            Atual
                          </span>
                        )}
                      </div>
                      
                      <p className={`text-2xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        R$ {parseFloat(plano.preco).toFixed(2)}
                        <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>/mês</span>
                      </p>
                      
                      {plano.descricao && (
                        <p className={`text-xs mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {plano.descricao}
                        </p>
                      )}
                      
                      <div className={`space-y-1 mb-4 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <div className="flex items-center justify-between">
                          <span>Mensagens:</span>
                          <span className="font-semibold">
                            {plano.limite_mensagens_mes === -1 ? '∞' : plano.limite_mensagens_mes}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Usuários:</span>
                          <span className="font-semibold">
                            {plano.limite_usuarios === -1 ? '∞' : plano.limite_usuarios}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Contatos:</span>
                          <span className="font-semibold">
                            {plano.limite_contatos === -1 ? '∞' : plano.limite_contatos}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>Campanhas:</span>
                          <span className="font-semibold">
                            {plano.limite_campanhas === -1 ? '∞' : plano.limite_campanhas}
                          </span>
                        </div>
                      </div>
                      
                      <div className={`space-y-1 text-xs mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {plano.permite_ia && <div>✓ IA Assistant</div>}
                        {plano.permite_fluxos && <div>✓ Fluxos de conversa</div>}
                        {plano.permite_relatorios_avancados && <div>✓ Relatórios avançados</div>}
                        {plano.permite_integracao_api && <div>✓ Integração API</div>}
                        {plano.permite_suporte_prioritario && <div>✓ Suporte prioritário</div>}
                        {plano.permite_whatsapp_multiplo && <div>✓ WhatsApp múltiplo</div>}
                      </div>
                      
                      {!isAtual && (
                        <button
                          onClick={() => handleAlterarPlano(plano.id)}
                          className="w-full gradient-button py-2 text-sm text-white rounded hover:opacity-90 transition-opacity"
                        >
                          Selecionar Plano
                        </button>
                      )}
                      {isAtual && (
                        <div className="w-full py-2 text-sm text-center bg-blue-500 text-white rounded">
                          Plano Ativo
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowPlanModal(false);
                    setSelectedClientForPlan(null);
                  }}
                  className={`px-4 py-2 border rounded transition-colors ${
                    darkMode
                      ? 'text-gray-300 border-gray-600 hover:bg-zinc-700'
                      : 'text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminClients;